import { supabase } from "@/lib/supabaseClient";
import { sendTenantSupportMessageEmailNotification } from "@/lib/apiClient";

export const SUPPORT_ATTACHMENT_BUCKET = "support-attachments";
export const SUPPORT_ATTACHMENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
];
export const SUPPORT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

export function assertSupportAttachment(file) {
  if (!file) return;
  if (!SUPPORT_ATTACHMENT_TYPES.includes(file.type)) {
    throw new Error("Attach a JPG, PNG, WebP, or PDF file.");
  }
  if (file.size > SUPPORT_ATTACHMENT_MAX_BYTES) {
    throw new Error("Attachments must be 10MB or smaller.");
  }
}

export async function uploadSupportAttachment({ file, clinicId, conversationId = "new" }) {
  assertSupportAttachment(file);
  const extension = file.name?.split(".").pop()?.toLowerCase() || "bin";
  const objectName = `${crypto.randomUUID?.() || Date.now()}.${extension}`;
  const path = `${clinicId || "clinic"}/${conversationId}/${objectName}`;
  const { error } = await supabase.storage
    .from(SUPPORT_ATTACHMENT_BUCKET)
    .upload(path, file, {
      cacheControl: "3600",
      contentType: file.type,
      upsert: false,
    });
  if (error) throw error;
  const { data } = supabase.storage.from(SUPPORT_ATTACHMENT_BUCKET).getPublicUrl(path);
  return {
    url: data?.publicUrl || path,
    path,
    name: file.name || objectName,
    mime_type: file.type,
    size_bytes: file.size,
  };
}

export async function listBubbleSupportMessages({ markRead = true } = {}) {
  const { data, error } = await supabase.rpc("list_support_bubble_messages", {
    p_limit: 100,
    p_mark_read: markRead,
  });
  if (error) throw error;
  return normalizeSupportThreadPayload(data);
}

export async function markSupportMessageSoundPlayed(messageId) {
  if (!messageId) return false;
  const { data, error } = await supabase.rpc("mark_support_message_sound_played", {
    p_message_id: messageId,
  });
  if (error) throw error;
  return Boolean(data);
}

export async function sendBubbleSupportMessage({ body, attachment }) {
  const { data, error } = await supabase.rpc("send_support_bubble_message", {
    p_body: body,
    p_attachment: attachment || null,
  });
  if (error) throw error;
  const payload = normalizeSupportThreadPayload(data);
  await notifyTenantSupportMessage(payload);
  return payload;
}

export async function listSupportConversations() {
  const { data, error } = await supabase.rpc("list_support_conversations");
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function getSupportConversation(conversationId) {
  const { data, error } = await supabase.rpc("get_support_conversation", {
    p_conversation_id: conversationId,
  });
  if (error) throw error;
  return normalizeSupportThreadPayload(data);
}

export async function createSupportConversation({ subject, category, body, attachment }) {
  const { data, error } = await supabase.rpc("create_support_conversation", {
    p_subject: subject,
    p_category: category,
    p_body: body,
    p_attachment: attachment || null,
    p_source: "support_page",
  });
  if (error) throw error;
  const payload = normalizeSupportThreadPayload(data);
  await notifyTenantSupportMessage(payload);
  return payload;
}

export async function sendSupportConversationMessage({ conversationId, body, attachment }) {
  const { data, error } = await supabase.rpc("send_support_conversation_message", {
    p_conversation_id: conversationId,
    p_body: body,
    p_attachment: attachment || null,
  });
  if (error) throw error;
  const payload = normalizeSupportThreadPayload(data);
  await notifyTenantSupportMessage(payload);
  return payload;
}

export async function resolveSupportConversation(conversationId) {
  const { data, error } = await supabase.rpc("resolve_support_conversation", {
    p_conversation_id: conversationId,
  });
  if (error) throw error;
  return data;
}

export function normalizeSupportThreadPayload(payload) {
  if (!payload) return { conversation: null, messages: [], unread_count: 0 };
  if (Array.isArray(payload)) return { conversation: null, messages: payload, unread_count: 0 };
  return {
    conversation: payload.conversation || null,
    messages: Array.isArray(payload.messages) ? payload.messages : [],
    unread_count: Number(payload.unread_count || 0),
    pending_sound: payload.pending_sound || null,
  };
}

function latestClinicMessage(messages = []) {
  return [...messages]
    .filter((message) => message?.sender_role === "clinic" && message?.id)
    .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())[0] || null;
}

async function notifyTenantSupportMessage(payload) {
  const message = latestClinicMessage(payload.messages);
  const clinicId = message?.clinic_id || payload.conversation?.clinic_id;
  if (!clinicId || !message?.id) return;

  try {
    await sendTenantSupportMessageEmailNotification({
      clinicId,
      supportMessageId: message.id,
    });
  } catch {
    // Email notification is best-effort after the database write succeeds.
  }
}
